import { FC, memo, useEffect, useRef } from 'react';
import { VariableSizeList as List, ListChildComponentProps, VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { EpisodesActions } from 'app/store/Episodes/Episodes.slice';
// Selectors
import { selectEpisodesSort, selectFilteredEpisodes } from 'app/store/Episodes/Episodes.selectors';
// MUI
import { Box, SxProps, Typography } from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
// Components
import { Loader, Message } from 'app/components/Utilities';
import EpisodesListItem from './EpisodesListItem';

const EpisodesList:FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const listRef = useRef<VariableSizeList>(null);
  const itemHeights = useRef<Map<number, number>>(new Map());

  const episodes = useAppSelector(selectFilteredEpisodes);
  const sort = useAppSelector(selectEpisodesSort);

  const getItemSize = (index: number) => {
    return itemHeights.current.get(index) ?? 44;
  };

  if (!episodes) return <Loader />;
  if ( !episodes.length ) return <Message text={t('pages.episodes.noData')} />;

  const TitleBlock:FC<{ width: number, title:string, sx?:SxProps, sortBy?:string }> = ({ width, title, sx = {}, sortBy }) => {
    const isSortEnabled = sortBy !== undefined;

    const setSort = () => {
      if (!sortBy) return;
      const nextDirection = sort.sortBy === sortBy && sort.direction === 'asc' ? 'desc' : 'asc';
      dispatch(EpisodesActions.setSort({ sortBy, direction: nextDirection }));
    }

    return (
      <Box onClick={setSort} sx={{
        width: `${width}px`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: isSortEnabled ? 'pointer' : 'default',
        ...sx
      }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {isSortEnabled ? (
          sort.sortBy === sortBy && sort.direction === 'desc' ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
        ) : null}
      </Box>
    )
  };

  const Row = ({ index, style }: ListChildComponentProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const node = ref.current;
      if (node) {
        const height = node.getBoundingClientRect().height;
        if (height !== itemHeights.current.get(index)) {
          itemHeights.current.set(index, height);
          listRef.current?.resetAfterIndex(index);
        }
      }
    }, [index]);

    return (
      <Box style={style} sx={{ borderBottom: '1px solid #ddd', '&:last-child': { borderBottom: 'none' } }}>
        <div ref={ref}>
          <EpisodesListItem episode={episodes[index]} />
        </div>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: 'auto', border: '1px solid #ddd', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden',
      display: 'flex', flexDirection: 'column'
    }}>
      <Box sx={{ width: '100%', bgcolor: '#eee', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 4 }}>
        <TitleBlock width={60} title="" />
        <TitleBlock width={180} title={t('pages.episodes.labels.dateTime')} sortBy="date" />
        <TitleBlock width={260} title={t('pages.episodes.labels.type')} sortBy="type" />
        <TitleBlock width={260} title={t('pages.episodes.labels.author')} sortBy="author" />
        {/* <TitleBlock width={150} title={t('pages.episodes.labels.tag')} /> */}
        <TitleBlock width={240} title={t('pages.episodes.labels.labels')} />
        <TitleBlock width={70} title={t('pages.episodes.labels.colors')} />
        <TitleBlock width={132} title={t('pages.episodes.labels.actions')} />
      </Box>
      <AutoSizer>
        {({ height, width }:{height:number, width:number}) => (
          <List
            ref={listRef}
            height={height - 40}
            width={width}
            itemCount={episodes.length}
            itemSize={getItemSize}
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    </Box>
  );
};

export default memo(EpisodesList);
